export type LetterState = "correct" | "present" | "absent" | "empty";
export type GameMode = "daily" | "free";
export type GameStatus = "playing" | "won" | "lost";

export type GuessRow = {
  word: string;
  result: LetterState[];
};

export const WORD_LENGTH = 5;
export const MAX_ATTEMPTS = 6;

export const targetWords = [
  "amore",
  "festa",
  "cuore",
  "notte",
  "piano",
  "verde",
  "rosso",
  "linea",
  "dolce",
  "forte",
  "fiore",
  "vento",
  "legno",
  "mondo",
  "sogno",
  "tempo",
  "gioco",
  "libro",
  "carta",
  "prato",
  "cielo",
  "acqua",
  "terra",
  "fuoco",
  "nuovo",
  "canto",
  "campo",
  "ponte",
  "porta",
  "stile",
  "costa",
  "frase",
  "prova",
  "miele",
  "scala",
  "treno",
  "posta",
  "volto",
  "fiume",
  "ombra",
  "punta",
  "lista",
  "mossa",
  "ruota",
  "forno",
  "latte",
  "pesca",
  "tigre",
  "isola",
  "corso"
];

export const acceptedWords = Array.from(
  new Set([
    ...targetWords,
    "abito",
    "agile",
    "aiuto",
    "albero",
    "amica",
    "amico",
    "anima",
    "apice",
    "aroma",
    "asilo",
    "avere",
    "bagno",
    "banco",
    "barca",
    "basso",
    "bello",
    "bocca",
    "borsa",
    "bravo",
    "breve",
    "caldo",
    "calma",
    "carne",
    "carro",
    "cassa",
    "certa",
    "certo",
    "chiaro",
    "cifra",
    "colpo",
    "conto",
    "coppa",
    "corpo",
    "credo",
    "danza",
    "dente",
    "detto",
    "donna",
    "dorso",
    "erba",
    "fermo",
    "firma",
    "forma",
    "forse",
    "frigo",
    "frutto",
    "gatto",
    "gente",
    "giallo",
    "gioia",
    "grado",
    "grano",
    "guida",
    "lampo",
    "largo",
    "legno",
    "lenta",
    "lento",
    "linea",
    "lotta",
    "madre",
    "magro",
    "marca",
    "marzo",
    "mezzo",
    "misto",
    "molto",
    "monte",
    "morte",
    "nave",
    "nervo",
    "noble",
    "norma",
    "occhi",
    "onore",
    "opera",
    "ordine",
    "padre",
    "paese",
    "palla",
    "parte",
    "passo",
    "patto",
    "pelle",
    "pieno",
    "pista",
    "poeta",
    "prato",
    "prezzo",
    "primo",
    "radio",
    "reale",
    "resto",
    "ricco",
    "riga",
    "ritmo",
    "salto",
    "santo",
    "scena",
    "sedia",
    "segno",
    "senso",
    "serie",
    "sesto",
    "sfera",
    "suono",
    "tanto",
    "tazza",
    "tetto",
    "torre",
    "trama",
    "udito",
    "umido",
    "valle",
    "vasto",
    "vetro",
    "viola",
    "vista",
    "visto",
    "voce",
    "vuoto",
    "zucca"
  ].filter((word) => normalizeWord(word).length === WORD_LENGTH))
);

export function normalizeWord(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z]/g, "")
    .slice(0, WORD_LENGTH);
}

export function getDailyWord(now = new Date()) {
  const start = Date.UTC(2026, 0, 1);
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const index = Math.max(0, Math.floor((today - start) / 86400000));
  return targetWords[index % targetWords.length];
}

export function getFreeWord(seed = Date.now()) {
  return targetWords[Math.abs(seed) % targetWords.length];
}

export function isAcceptedWord(word: string) {
  return acceptedWords.includes(normalizeWord(word));
}

export function scoreGuess(rawGuess: string, rawTarget: string): LetterState[] {
  const guess = normalizeWord(rawGuess);
  const target = normalizeWord(rawTarget);
  const result: LetterState[] = Array(WORD_LENGTH).fill("absent");
  const remaining = new Map<string, number>();

  for (let index = 0; index < WORD_LENGTH; index += 1) {
    if (guess[index] === target[index]) {
      result[index] = "correct";
    } else {
      remaining.set(target[index], (remaining.get(target[index]) ?? 0) + 1);
    }
  }

  for (let index = 0; index < WORD_LENGTH; index += 1) {
    if (result[index] === "correct") {
      continue;
    }
    const letter = guess[index];
    const count = remaining.get(letter) ?? 0;
    if (count > 0) {
      result[index] = "present";
      remaining.set(letter, count - 1);
    }
  }

  return result;
}

export function mergeKeyboardState(
  previous: Record<string, LetterState>,
  word: string,
  result: LetterState[]
) {
  const rank: Record<LetterState, number> = {
    empty: 0,
    absent: 1,
    present: 2,
    correct: 3
  };
  const next = { ...previous };
  normalizeWord(word)
    .split("")
    .forEach((letter, index) => {
      const state = result[index];
      if (rank[state] > rank[next[letter] ?? "empty"]) {
        next[letter] = state;
      }
    });
  return next;
}
